/**
 * useNavBadges — single source for every sidebar navigation badge count.
 *
 * Replaces ~40 individual per-badge queries (each its own HTTP request, fired by
 * every open tab) with ONE request to /nav-badges/, fetched by a single elected
 * leader tab (useTabLeader) and broadcast to the rest. Net effect: a whole browser
 * makes one nav-badge request per interval regardless of how many tabs are open.
 *
 * Followers never hit the network: they receive the leader's broadcast and read it
 * from the query cache. A freshly opened follower (no data yet) asks the leader for
 * an immediate broadcast via requestRefresh().
 */

import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import apiClient from "@/api/client";
import type { B2BBadgeCounts } from "@/features/b2b_invoices/badges";
import { useAuthStore } from "@/store/auth";
import { useTabLeader } from "@/hooks/useTabLeader";

export interface TenderActionCounts {
  needs_pricing: number;
  pricing_claimed: number;
  needs_approval: number;
  needs_offer: number;
  offer_submitted: number;
  needs_result: number;
}

export interface NavBadges {
  tenders_pending_approvals: number;
  tender_action_counts: TenderActionCounts;
  ze_tender_action_counts: TenderActionCounts;
  power_projects_attention: number;
  /** Ponaglenia (telefony kontrolne) tej osoby na dziś — łącznie z przeniesionymi. */
  control_calls_due: number;
  /** Wnioski urzędowe w kolejce „Do akceptacji dyrektora", które ta osoba może zatwierdzić
   *  (liczone tylko dla dyr. technicznego / CEO z DLC_01). */
  applications_pending_approval: number;
  correspondence: {
    to_acknowledge: number;
    to_file: number;
    to_send: number;
    returned: number;
    /** Moje pisma wychodzące czekające w kolejce wysyłki działu obiegu (self-scoped). */
    my_dispatch: number;
  };
  orders_pending: number;
  /** Projekty ZE prowadzone przez firmę tego konta (apps.contractor_led). */
  led_projects: number;
  /** Tytuły prawne zgłoszone przez firmy do naszego potwierdzenia — jedyny sygnał modułu. */
  led_titles_to_confirm: number;
  protocols_pending: number;
  protocols_needing_invoice: number;
  protocols_needing_hr_mandate: number;
  weekly_report_pending: number;
  weekly_report_unread: number;
  requisitions_pending: number;
  orders_change_requests_pending: number;
  orders_external_costs_pending: number;
  orders_stale_unconfirmed: number;
  orders_pending_comments: number;
  /** Odebrane zamówienia bez oceny podwykonawcy, za które odpowiada TEN użytkownik. */
  orders_awaiting_my_rating: number;
  result_files_uploaded: number;
  invoices_pending: number;
  invoices_needs_action: number;
  prz_investor: number;
  prz_draft: number;
  prz_needs_approval: number;
  prz_proforma_to_convert: number;
  knowledge_pending: number;
  /** Write proposals awaiting my approve/reject click, split by their origin. */
  aron_pending_writes: { chat: number; mail: number };
  /** Results of scheduled ARON tasks nobody has opened yet ("nowy wynik"). */
  aron_scheduled_unread: number;
  /** Kopie robocze eop@ czekające na wysyłkę (tylko z dostępem do skrzynki eop@). */
  eop_drafts_waiting: number;
  eng_reviews_to_review: number;
  official_fee_pending_acknowledgement: number;
  official_fee_pending_approval: number;
  official_fee_pending_payment: number;
  /** Faktury B2B per powód: zaległe miesiące / odrzucone / szkice (pracownik B2B),
   *  do zatwierdzenia (CEO), do rozliczenia (księgowość). */
  b2b_invoices: B2BBadgeCounts;
  self_allocation_action: number;
  doc_pending: number;
  doc_awaiting: number;
  doc_unacknowledged: number;
  timesheets_pending: number;
  supplies_pending: number;
  assets_pending: number;
  couriers_pending: number;
  print_orders_pending: number;
  insurance_alert: number;
  today_interviews: number;
  expiring_contracts: number;
  health_todo: number;
  fleet_todo: { open_issues: number; expiring_soon: number; total: number };
  rfq_scope_requests_pending: number;
  rfq_open_proceedings: number;
  rfq_to_resolve: number;
  rfq_account_requests: number;
  leaves_pending: number;
  sick_leaves_pending: number;
  remote_work_pending: number;
  dev_issues_open: number;
  subtasks_my_active: number;
  expenses: { pending_acceptance: number; pending_payment: number; pending_booking: number };
  credentials_alert: number;
  last_test_run_bugs: number;
  payroll_actuals_missing: number;
  timesheet_missing: boolean;
  whats_new_unread: number;
}

const EMPTY_TENDER_COUNTS: TenderActionCounts = {
  needs_pricing: 0,
  pricing_claimed: 0,
  needs_approval: 0,
  needs_offer: 0,
  offer_submitted: 0,
  needs_result: 0,
};

export const EMPTY_NAV_BADGES: NavBadges = {
  tenders_pending_approvals: 0,
  tender_action_counts: EMPTY_TENDER_COUNTS,
  ze_tender_action_counts: EMPTY_TENDER_COUNTS,
  power_projects_attention: 0,
  control_calls_due: 0,
  applications_pending_approval: 0,
  correspondence: { to_acknowledge: 0, to_file: 0, to_send: 0, returned: 0, my_dispatch: 0 },
  orders_pending: 0,
  led_projects: 0,
  led_titles_to_confirm: 0,
  protocols_pending: 0,
  protocols_needing_invoice: 0,
  protocols_needing_hr_mandate: 0,
  weekly_report_pending: 0,
  weekly_report_unread: 0,
  requisitions_pending: 0,
  orders_change_requests_pending: 0,
  orders_external_costs_pending: 0,
  orders_stale_unconfirmed: 0,
  orders_pending_comments: 0,
  orders_awaiting_my_rating: 0,
  result_files_uploaded: 0,
  invoices_pending: 0,
  invoices_needs_action: 0,
  prz_investor: 0,
  prz_draft: 0,
  prz_needs_approval: 0,
  prz_proforma_to_convert: 0,
  knowledge_pending: 0,
  aron_pending_writes: { chat: 0, mail: 0 },
  aron_scheduled_unread: 0,
  eop_drafts_waiting: 0,
  eng_reviews_to_review: 0,
  official_fee_pending_acknowledgement: 0,
  official_fee_pending_approval: 0,
  official_fee_pending_payment: 0,
  b2b_invoices: { missing_periods: 0, rejected: 0, drafts: 0, to_approve: 0, to_settle: 0 },
  self_allocation_action: 0,
  doc_pending: 0,
  doc_awaiting: 0,
  doc_unacknowledged: 0,
  timesheets_pending: 0,
  supplies_pending: 0,
  assets_pending: 0,
  couriers_pending: 0,
  print_orders_pending: 0,
  insurance_alert: 0,
  today_interviews: 0,
  expiring_contracts: 0,
  health_todo: 0,
  fleet_todo: { open_issues: 0, expiring_soon: 0, total: 0 },
  rfq_scope_requests_pending: 0,
  rfq_open_proceedings: 0,
  rfq_to_resolve: 0,
  rfq_account_requests: 0,
  leaves_pending: 0,
  sick_leaves_pending: 0,
  remote_work_pending: 0,
  dev_issues_open: 0,
  subtasks_my_active: 0,
  expenses: { pending_acceptance: 0, pending_payment: 0, pending_booking: 0 },
  credentials_alert: 0,
  last_test_run_bugs: 0,
  payroll_actuals_missing: 0,
  timesheet_missing: false,
  whats_new_unread: 0,
};

export const NAV_BADGES_KEY = ["nav-badges"] as const;

/** How long we let the shared (leader → broadcast) path deliver before this tab
 *  fetches on its own. Long enough for a normal round-trip, short enough that a
 *  broken leader is not something the user stares at. */
const SELF_HEAL_GRACE_MS = 4_000;

/** Retry cadence while the sidebar still has nothing to show. */
const SELF_HEAL_RETRY_MS = 20_000;

export async function fetchNavBadges(): Promise<NavBadges> {
  const r = await apiClient.get<{ data: NavBadges }>("/nav-badges/");
  return r.data.data;
}

/** Write a payload into the shared cache, ignoring anything that is not a badge
 *  object. `setQueryData(key, undefined)` is a SILENT no-op in TanStack Query, so
 *  a malformed response would otherwise leave every reader on `undefined` — i.e.
 *  a permanent spinner and blank badges — while the polling keeps "succeeding".
 *  Returns whether the cache now holds data. */
function applyBadges(qc: ReturnType<typeof useQueryClient>, payload: unknown): boolean {
  if (!payload || typeof payload !== "object") return false;
  qc.setQueryData(NAV_BADGES_KEY, payload as NavBadges);
  return true;
}

// ── Cross-tab "was this fetched a moment ago?" snapshot ───────────────────────
//
// Leader-election alone still lets EACH freshly opened tab ask the leader to
// fetch (empty cache → requestRefresh). Opening many tabs in quick succession
// therefore still hammers the server, just via the leader. To stop that we keep
// the last result — with a timestamp — in localStorage, shared by all tabs.

const SNAPSHOT_KEY = "elg5-nav-badges-snapshot";

/** A shared snapshot younger than this is shown to a newly opened tab instantly,
 *  with no network at all. Matches the leader poll interval, so it's never more
 *  than one poll cycle stale (the leader broadcasts an update within it). */
const SNAPSHOT_FRESH_MS = 90_000;

/** How often the elected leader tab polls /nav-badges/. Paired with the backend's
 *  per-user SWR cache (NAV_BADGES_CACHE_FRESH_SECONDS = 120s): the badge payload a
 *  user sees is at most ~poll+fresh old, and a recompute runs at most every other
 *  poll per user. Raising either linearly cuts requests/DB load. */
const POLL_MS = 90_000;

/** The leader collapses fetch requests arriving within this window into ONE network
 *  call (re-broadcasting the last snapshot instead). Bounds the cold-start burst
 *  when several tabs open at once; the poll tick always exceeds it, so periodic
 *  refresh is unaffected. */
const FETCH_COALESCE_MS = 5_000;

interface Snapshot {
  data: NavBadges;
  at: number;
}

function readSnapshot(): Snapshot | null {
  try {
    const raw = localStorage.getItem(SNAPSHOT_KEY);
    if (!raw) return null;
    const snap = JSON.parse(raw) as Snapshot;
    return snap && typeof snap.at === "number" && snap.data ? snap : null;
  } catch {
    return null; // private mode / disabled storage → behave as if no snapshot
  }
}

function writeSnapshot(data: NavBadges): void {
  try {
    localStorage.setItem(SNAPSHOT_KEY, JSON.stringify({ data, at: Date.now() } satisfies Snapshot));
  } catch {
    /* quota / private mode — degrade to no shared snapshot */
  }
}

// ── Cross-tab "a fetch is already in flight" marker ───────────────────────────
//
// The snapshot above is written only AFTER a fetch returns, so it can't stop the
// cold-start burst: opening N tabs in quick succession (e.g. clicking several
// notifications) means every tab that mounts before the first fetch completes
// sees no fresh snapshot and asks the leader to fetch. So we ALSO record WHEN a
// refresh was requested — shared across tabs via localStorage — measured from the
// moment the request is SENT, not from when the result lands. A newly opened tab
// that sees a recent request knows "someone already kicked off a fetch; its
// broadcast will reach me" and waits instead of piling on its own request.

const REQUEST_SENT_KEY = "elg5-nav-badges-requested-at";

/** A request sent within this window is treated as still in flight: another tab's
 *  broadcast will deliver the result, so a newly opened tab skips its own request.
 *  Comfortably covers a slow fetch round-trip; if that fetch ultimately fails, new
 *  tabs only defer by this long before retrying (the periodic leader poll is the
 *  final backstop). Kept below SNAPSHOT_FRESH_MS: once a result lands the snapshot
 *  path takes over. */
const REQUEST_INFLIGHT_MS = 10_000;

function readRequestedAt(): number {
  try {
    return Number(localStorage.getItem(REQUEST_SENT_KEY) ?? 0);
  } catch {
    return 0; // private mode / disabled storage → behave as if never requested
  }
}

function markRequested(): void {
  try {
    localStorage.setItem(REQUEST_SENT_KEY, String(Date.now()));
  } catch {
    /* quota / private mode — degrade to no shared marker (may re-request) */
  }
}

/** True when this document load is a reload (F5 / Cmd-R / location.reload()), as
 *  opposed to opening a new tab or normal navigation. A reload should pull fresh
 *  data; a new tab may reuse a recent snapshot. */
function isReload(): boolean {
  try {
    const nav = performance.getEntriesByType("navigation")[0] as
      | PerformanceNavigationTiming
      | undefined;
    if (nav) return nav.type === "reload";
    // Legacy fallback (deprecated API): 1 === TYPE_RELOAD.
    return (performance as unknown as { navigation?: { type?: number } }).navigation?.type === 1;
  } catch {
    return false;
  }
}

/**
 * Mount ONCE, high in the tree (the Sidebar). Returns the latest badge counts.
 * The data is leader-fetched and cross-tab-broadcast; this tab may never touch
 * the network.
 */
export function useNavBadges(): NavBadges {
  const qc = useQueryClient();
  const enabled = useAuthStore((s) => !!s.user);

  // Cache-only: the leader writes here (and to peers); we never auto-fetch.
  const { data } = useQuery({
    queryKey: NAV_BADGES_KEY,
    queryFn: fetchNavBadges,
    enabled: false,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  // Leader-side coalescing state (only the leader tab reads/writes these).
  const inFlightRef = useRef(false);
  const lastFetchAtRef = useRef(0);

  const { requestRefresh } = useTabLeader(
    {
      channelName: "elg5-nav-badges-sync",
      storageKey: "elg5-nav-badges-leader-ts",
      enabled,
      pollMs: POLL_MS,
    },
    {
      onLeaderPoll: async ({ broadcast }) => {
        // Collapse bursts: if a fetch is already running, or one finished within
        // FETCH_COALESCE_MS, re-broadcast the last snapshot instead of hitting the
        // server again. The poll tick always exceeds the window, so periodic
        // refresh still fetches fresh.
        if (inFlightRef.current) return;
        const now = Date.now();
        if (now - lastFetchAtRef.current < FETCH_COALESCE_MS) {
          const snap = readSnapshot();
          if (snap) {
            qc.setQueryData(NAV_BADGES_KEY, snap.data);
            broadcast(snap.data);
          }
          return;
        }
        inFlightRef.current = true;
        try {
          const badges = await fetchNavBadges();
          lastFetchAtRef.current = Date.now();
          if (!applyBadges(qc, badges)) return;
          writeSnapshot(badges);
          broadcast(badges);
        } catch {
          // Network blip / token refresh that ended badly: keep whatever we show
          // and let the next tick (or the self-heal timer below) try again. An
          // uncaught rejection here would leave every tab on the spinner.
        } finally {
          inFlightRef.current = false;
        }
      },
      onMessage: (payload) => {
        applyBadges(qc, payload);
      },
    },
  );

  // Self-heal: nothing to show yet.
  //
  // Everything above routes the payload through the elected leader — and that has
  // failure modes a follower cannot see: the leader's lease can be held by a tab
  // that just closed or logged out (its heartbeat outlives it by design), a
  // BroadcastChannel can be partitioned away from us (container tabs, private
  // windows), and the leader's own fetch can fail. In all of them our
  // `requestRefresh()` goes into an empty room and the sidebar sits on the
  // spinner with every badge blank, even though the endpoint answers fine.
  //
  // So: if the payload has not arrived shortly after we asked, stop waiting for
  // anybody and fetch for ourselves, retrying until something lands. Costs one
  // request per tab in exactly the case where the shared path is broken.
  useEffect(() => {
    if (!enabled || data !== undefined) return;
    let stop = false;
    const tick = async () => {
      if (stop || qc.getQueryData(NAV_BADGES_KEY) !== undefined) return;
      try {
        const badges = await fetchNavBadges();
        if (stop) return;
        if (applyBadges(qc, badges)) writeSnapshot(badges);
      } catch {
        /* try again on the next tick */
      }
      if (!stop) timer = setTimeout(tick, SELF_HEAL_RETRY_MS);
    };
    let timer = setTimeout(tick, SELF_HEAL_GRACE_MS);
    return () => {
      stop = true;
      clearTimeout(timer);
    };
  }, [enabled, data, qc]);

  // First paint of a freshly loaded document (new tab, navigation, or reload).
  useEffect(() => {
    if (!enabled || data !== undefined) return;
    // A reload forces fresh data. A new tab / normal navigation may reuse work
    // another tab did moments ago — zero network.
    if (!isReload()) {
      // (a) A recent RESULT is on hand → show it, done.
      const snap = readSnapshot();
      if (snap && Date.now() - snap.at < SNAPSHOT_FRESH_MS && applyBadges(qc, snap.data)) {
        return;
      }
      // (b) No result yet, but another freshly opened tab already asked the
      // leader to fetch a moment ago. Its broadcast will populate our cache
      // shortly (our BroadcastChannel is open by the time this effect runs, well
      // before any network response can return), so we must NOT fire a second
      // request. Gating on "request sent at" — not "result written at" — is what
      // stops the burst: opening many tabs from notifications no longer has each
      // one request while the first fetch is still in flight.
      const requestedAt = readRequestedAt();
      if (requestedAt && Date.now() - requestedAt < REQUEST_INFLIGHT_MS) return;
    }
    // Publish the "request in flight" marker BEFORE asking, so sibling tabs
    // opening right after us see it and stand down.
    markRequested();
    requestRefresh();
  }, [enabled, data, requestRefresh, qc]);

  return data ?? EMPTY_NAV_BADGES;
}

/**
 * Cache-only reader of the shared nav-badge counts. Unlike useNavBadges it never
 * fetches and never joins leader election, so it is safe to call from any component
 * (useNavBadges must be mounted exactly once — in the Sidebar — which is what keeps
 * this cache populated). Returns EMPTY_NAV_BADGES until the first payload lands.
 */
export function useNavBadgeCounts(): NavBadges {
  const { data } = useQuery({
    queryKey: NAV_BADGES_KEY,
    queryFn: fetchNavBadges,
    enabled: false,
    staleTime: Infinity,
    gcTime: Infinity,
  });
  return data ?? EMPTY_NAV_BADGES;
}

/**
 * Companion to useNavBadges: true while the first badge payload for this session
 * has not arrived yet (authenticated, but nothing in the cache). Since the counts
 * are written via setQueryData (leader fetch / cross-tab broadcast / snapshot),
 * `data === undefined` is the only reliable "still loading" signal — once any
 * payload lands the cache stays populated (gcTime Infinity), so periodic background
 * polls refresh silently without re-triggering the spinner.
 */
export function useNavBadgesLoading(): boolean {
  const enabled = useAuthStore((s) => !!s.user);
  // Cache-only observer on the same key; never fetches on its own.
  const { data } = useQuery({
    queryKey: NAV_BADGES_KEY,
    queryFn: fetchNavBadges,
    enabled: false,
    staleTime: Infinity,
    gcTime: Infinity,
  });
  return enabled && data === undefined;
}
